import * as React from "react";
import { Alert, Button, Stack, Typography } from "@mui/material";

import { connectOAuth, OAuthFlowError } from "@/lib/oauth/flow";
import {
	oauthErrorMessage,
	useCompleteOAuth,
	useDisconnectOAuth,
	useOAuthStatus,
	useStartOAuth,
} from "@/hooks/api/use-oauth";
import { ConfirmModal } from "@/components/confirm-modal/confirm-modal";
import { toast } from "@/components/core/toaster";

export function OAuthConnection({ pluginId }: { pluginId: string }): React.JSX.Element | null {
	const { data } = useOAuthStatus(pluginId);
	const { mutateAsync: start } = useStartOAuth();
	const { mutateAsync: complete } = useCompleteOAuth();
	const disconnect = useDisconnectOAuth();
	/** Set while a connect flow runs; aborting it is the Cancel button. */
	const [connecting, setConnecting] = React.useState<AbortController | null>(null);
	const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);

	if (!data?.configured) return null;

	const handleConnect = () => {
		const controller = new AbortController();
		setConnecting(controller);

		// No await before this call: it opens the popup, which the browser only allows inside the click.
		// The flow is not cancelled when the drawer closes: the popup stays in front of the user, and
		// finishing it still connects the account.
		connectOAuth({ start: () => start({ pluginId }), complete, signal: controller.signal })
			.catch((error) => {
				if (error instanceof OAuthFlowError && error.reason === "cancelled") return;
				toast.error(oauthErrorMessage(error));
			})
			.finally(() => setConnecting(null));
	};

	return (
		<Stack spacing={1.5}>
			<Typography variant="subtitle2">Account connection</Typography>

			{data.status === "connected" && (
				<Alert severity="success">{data.caption ? `Connected as ${data.caption}` : "Connected"}</Alert>
			)}
			{data.status === "reconnect_required" && (
				<Alert severity="warning">
					{data.caption ? `The connection to ${data.caption} stopped working.` : "The connection stopped working."}{" "}
					Connect the account again.
				</Alert>
			)}
			{data.status === "disconnected" && (
				<Typography variant="body2" color="text.secondary">
					No account connected.
				</Typography>
			)}

			{data.status === "connected" ? (
				<Stack direction="row">
					<Button
						variant="outlined"
						color="error"
						loading={disconnect.isPending}
						onClick={() => setIsConfirmOpen(true)}
					>
						Disconnect
					</Button>
				</Stack>
			) : (
				<Stack spacing={1}>
					<Stack direction="row" spacing={1}>
						<Button variant="contained" loading={connecting !== null} onClick={handleConnect}>
							{data.status === "reconnect_required" ? "Reconnect" : "Connect"}
						</Button>
						{connecting && (
							<Button variant="text" onClick={() => connecting.abort()}>
								Cancel
							</Button>
						)}
					</Stack>
					{connecting && (
						<Typography variant="body2" color="text.secondary">
							Finish connecting in the window that opened.
						</Typography>
					)}
				</Stack>
			)}

			<ConfirmModal
				open={isConfirmOpen}
				onClose={() => setIsConfirmOpen(false)}
				onSumbit={() => disconnect.mutateAsync(pluginId)}
				title="Disconnect account"
				description="The plugin loses access to the account and the grant is revoked at the provider."
			/>
		</Stack>
	);
}
